import { Alert, Button, Group, Modal, NumberInput, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { formatQty, typedNumber } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { LEDGER_PATH, STOCK_PATH, useStockItems, useStockOnHand } from './stock';
import { Known, type StockActionProps } from './subject';

/**
 * Logging waste — a dropped bottle, a crate of limes gone over — is the most frequent thing that
 * happens to stock outside a sale, and until now the page named it as the remedy without offering
 * it. The form asks only what the manager knows standing in the walk-in: what, where, how much,
 * why. The unit comes from the item, and the cost from what the bar has been paying.
 *
 * Opened from a stock item, it already knows what was thrown away, and from the row for one shelf
 * it already knows where — which is the difference between correcting a figure you are looking at
 * and re-describing it from a blank form.
 */
export function LogWasteButton({
  inventoryItemId,
  storageLocationId,
  label,
  compact,
}: StockActionProps = {}) {
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);
  const items = useEntityIndex('inventoryItem');
  const areas = useEntityIndex('storageLocation');
  const reasons = useEntityIndex('wasteReason');
  const stock = useStockItems();

  const form = useForm({
    initialValues: {
      inventoryItemId: inventoryItemId ?? '',
      storageLocationId: storageLocationId ?? '',
      qty: '' as number | '',
      reasonCode: '',
    },
    validate: {
      inventoryItemId: (v) => (v ? null : 'Choose what was thrown away'),
      storageLocationId: (v) => (v ? null : 'Choose where it was kept'),
      reasonCode: (v) => (v ? null : 'Choose why it was thrown away'),
      qty: (v) => (typeof v === 'number' && v > 0 ? null : 'Enter how much was thrown away'),
    },
  });

  const values = form.getValues();
  const unit = stock.unitOf(values.inventoryItemId);
  const itemName = items.label(values.inventoryItemId);

  /**
   * What the books say is on the shelf for the item chosen, area by area.
   *
   * Logging five kilos of limes out of an area holding none was accepted in silence and left that
   * shelf reading −5 kg — a figure that then feeds what a shelf is worth, what can be made, and the
   * variance report, with nothing anywhere saying it happened. The form told the manager it could
   * not be undone and then let him do the one thing he cannot have meant.
   *
   * Read per item rather than per item AND area, because one request answers both questions worth
   * asking: how much is here, and — the usual cause of a negative — where the stock actually is.
   * Nothing is refused. The books can genuinely be behind what a bin holds, and a rejected waste
   * slip is a bottle nobody writes down at all; say what will happen and let the person decide.
   */
  const { rows: onHand } = useStockOnHand(undefined, values.inventoryItemId || undefined);
  const here = onHand.find((r) => r.storageLocationId === values.storageLocationId)?.qtyOnHand ?? 0;
  const elsewhere = onHand.filter((r) => r.storageLocationId !== values.storageLocationId && r.qtyOnHand > 0);
  const wasting = typeof values.qty === 'number' ? values.qty : 0;
  const overdrawn = Boolean(values.inventoryItemId && values.storageLocationId) && wasting > here;

  const close = () => {
    setOpened(false);
    form.reset();
  };

  const log = useMutation({
    mutationFn: (v: typeof values) =>
      rawApi('POST', '/admin/v1/inventory/waste', {
        inventoryItemId: v.inventoryItemId,
        storageLocationId: v.storageLocationId,
        qty: Number(v.qty),
        unit,
        reasonCode: v.reasonCode,
      }),
    onSuccess: (_result, v) => {
      void queryClient.invalidateQueries({ queryKey: listKey(STOCK_PATH) });
      void queryClient.invalidateQueries({ queryKey: listKey(LEDGER_PATH) });
      notifyDone(
        'Waste logged',
        `${formatQty(typedNumber(v.qty) ?? 0, unit)} of ${itemName} is off the shelf figure.`,
      );
      close();
    },
    onError: (err) => notifyError(err, 'log this waste'),
  });

  // An item with no unit cannot be recorded honestly, and guessing one here would put a movement
  // into the ledger in a unit nothing else uses.
  const missingUnit = Boolean(values.inventoryItemId) && !unit;
  const noReasons = !reasons.isLoading && !reasons.isError && reasons.options.length === 0;

  return (
    <>
      <Button
        size={compact ? 'compact-xs' : 'sm'}
        variant={compact ? 'subtle' : 'light'}
        color="red"
        onClick={() => setOpened(true)}
      >
        {label ?? 'Log waste'}
      </Button>
      <Modal opened={opened} onClose={close} title="Log waste" size="lg" closeOnClickOutside={false}>
        <form onSubmit={form.onSubmit((v) => log.mutate(v))}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              This takes the amount off what you have on hand straight away and counts what it cost against
              the bar. It cannot be undone — if you get it wrong, count the area again to put the figure
              right.
            </Text>

            {noReasons && (
              <Alert color="yellow" title="No reasons set up yet">
                Waste has to be recorded against a reason. Add a few on the Settings screen — broken, spoiled,
                spilled — and they will appear here.
              </Alert>
            )}

            {inventoryItemId ? (
              <Known label="What was thrown away" value={itemName} />
            ) : (
              <EntitySelect
                entity="inventoryItem"
                label="What was thrown away"
                required
                value={values.inventoryItemId || null}
                onChange={(v) => form.setFieldValue('inventoryItemId', v ?? '')}
                error={form.errors.inventoryItemId}
              />
            )}

            {storageLocationId ? (
              <Known label="Where it was kept" value={areas.label(storageLocationId)} />
            ) : (
              <EntitySelect
                entity="storageLocation"
                label="Where it was kept"
                description="Stock is counted separately in each area, so this decides which shelf figure drops"
                required
                value={values.storageLocationId || null}
                onChange={(v) => form.setFieldValue('storageLocationId', v ?? '')}
                error={form.errors.storageLocationId}
              />
            )}

            <NumberInput
              label="How much"
              required
              min={0}
              suffix={unit ? ` ${unit}` : undefined}
              description={
                missingUnit
                  ? undefined
                  : unit
                    ? `Counted in ${unit}, the same as everywhere else for this item`
                    : 'Pick the item first and this will show what it is counted in'
              }
              error={
                missingUnit
                  ? 'This item has no unit set — fill in "Counted in" under Stock items'
                  : form.errors.qty
              }
              {...form.getInputProps('qty')}
            />

            <EntitySelect
              entity="wasteReason"
              label="Why"
              required
              value={values.reasonCode || null}
              onChange={(v) => form.setFieldValue('reasonCode', v ?? '')}
              error={form.errors.reasonCode}
            />

            {/* Said in the unit the item is counted in, and as the figure the shelf will be left
                showing — not as "insufficient stock", which is a sentence about a database. Where
                the stock actually is comes next, because picking the wrong area is what nearly
                always causes this and it is fixed by changing one field rather than by counting. */}
            {overdrawn && (
              <Alert color="yellow" title="More than this area is holding">
                <Text size="sm">
                  The books say there {here === 1 ? 'is' : 'are'} {formatQty(here, unit)} of {itemName} in{' '}
                  {areas.label(values.storageLocationId)}. Logging {formatQty(wasting, unit)} leaves that
                  shelf reading {formatQty(here - wasting, unit)}.
                </Text>
                {elsewhere.length > 0 && (
                  <Text size="sm" mt="xs">
                    There {elsewhere.length === 1 ? 'is' : 'is also'}{' '}
                    {elsewhere
                      .map((r) => `${formatQty(r.qtyOnHand, unit)} in ${areas.label(r.storageLocationId)}`)
                      .join(', ')}
                    . If that is where it came from, change the area above.
                  </Text>
                )}
                <Text size="sm" mt="xs">
                  If this really is what was thrown away, record it and then count the area — that is what
                  brings the shelf figure back to what is on it.
                </Text>
              </Alert>
            )}
          </Stack>
          <Group justify="flex-end" mt="xl">
            <Button variant="default" onClick={close} disabled={log.isPending}>
              Cancel
            </Button>
            <Button type="submit" color="red" loading={log.isPending} disabled={missingUnit}>
              Log waste
            </Button>
          </Group>
        </form>
      </Modal>
    </>
  );
}
